/**
 * @module SelectActivator
 *
 * @see https://0.vuetifyjs.com/components/forms/select
 *
 * @remarks
 * Trigger component for the select dropdown. Provides the button element
 * with combobox ARIA role, keyboard navigation, and CSS anchor positioning
 * for the dropdown content.
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useSelectContext } from './SelectRoot.vue'

  // Utilities
  import { isUndefined } from '#v0/utilities'
  import { toRef, toValue, useTemplateRef, watchEffect } from 'vue'

  // Types
  import type { AtomProps } from '#v0/components/Atom'

  export interface SelectActivatorProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
    /** Accessible label for the activator when no visible label is present */
    label?: string
    /** ID of the element that labels this activator */
    ariaLabelledby?: string
  }

  export interface SelectActivatorSlotProps {
    /** Whether the dropdown is open */
    isOpen: boolean
    /** Attributes to bind to the trigger element */
    attrs: {
      'id': string
      'role': 'combobox'
      'type': 'button' | undefined
      'aria-expanded': boolean
      'aria-haspopup': 'listbox'
      'aria-controls': string
      'aria-label': string | undefined
      'aria-labelledby': string | undefined
      'aria-disabled': boolean
      'disabled': true | undefined
      'tabindex': 0 | undefined
      'data-disabled': true | undefined
      'data-open': true | undefined
      'style': Record<string, string>
      'onClick': () => void
      'onKeydown': (e: KeyboardEvent) => void
    }
  }
</script>

<script setup lang="ts">
  defineOptions({ name: 'SelectActivator' })

  defineSlots<{
    default: (props: SelectActivatorSlotProps) => any
  }>()

  const {
    as = 'button',
    namespace = 'v0:select',
    renderless,
    label,
    ariaLabelledby,
  } = defineProps<SelectActivatorProps>()

  const context = useSelectContext(namespace)

  const activatorEl = useTemplateRef('activator')
  watchEffect(() => {
    context.activatorEl.value = activatorEl.value?.element ?? null
  })

  function onClick () {
    context.toggle()
  }

  function printable (e: KeyboardEvent) {
    return e.key.length === 1
      && e.key !== ' '
      && !e.ctrlKey
      && !e.metaKey
      && !e.altKey
      && !e.isComposing
  }

  function onKeydown (e: KeyboardEvent) {
    if (printable(e)) {
      e.preventDefault()
      context.onTypeahead(e.key)
      return
    }

    if (context.isOpen.value) {
      switch (e.key) {
        case ' ':
        case 'Enter': {
          e.preventDefault()
          if (!isUndefined(context.virtualFocus.highlightedId.value)) {
            context.select(context.virtualFocus.highlightedId.value)
          }
          break
        }
        case 'Escape': {
          e.preventDefault()
          context.close()
          break
        }
        case 'Tab': {
          context.close()
          break
        }
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Home':
        case 'End': {
          context.virtualFocus.onKeydown(e)
          break
        }
      }
    } else {
      switch (e.key) {
        case ' ':
        case 'Enter':
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault()
          context.open()
          break
        }
      }
    }
  }

  const slotProps = toRef((): SelectActivatorSlotProps => ({
    isOpen: context.isOpen.value,
    attrs: {
      'id': context.activatorId,
      'role': 'combobox',
      'type': as === 'button' ? 'button' : undefined,
      'aria-expanded': context.isOpen.value,
      'aria-haspopup': 'listbox',
      'aria-controls': context.listboxId,
      'aria-label': ariaLabelledby ? undefined : (label || undefined),
      'aria-labelledby': ariaLabelledby || undefined,
      'aria-disabled': toValue(context.disabled),
      'disabled': toValue(context.disabled) || undefined,
      'tabindex': as === 'button' ? undefined : 0,
      'data-disabled': toValue(context.disabled) || undefined,
      'data-open': context.isOpen.value || undefined,
      'style': context.popover.anchorStyles.value,
      onClick,
      onKeydown,
    },
  }))
</script>

<template>
  <Atom
    ref="activator"
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
